import { Vendor } from "@finway-group/shared/lib/models"
import { VendorRule } from "@finway-group/shared/lib/models/vendor/vendorRule.model"
import { Button, Form, Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { VendorRuleForm } from "~Components/forms/vendorRule.form"
import { useUpdateVendorRule } from "~Shared/queries/vendors/vendor.mutations"
import { NotificationService, VendorService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"

interface VendorRuleModalProps {
    isShowing: boolean
    vendor: Vendor
    onCancel: (state: boolean) => void
    onSubmit?: (rule: Pick<VendorRule, "costCenter" | "costCenter2" | "expenseAccount" | "expensePaymentFlowInformation" | "paymentTerm">) => void
}

export const VendorRuleModal = ({ isShowing, onCancel, vendor, onSubmit }: VendorRuleModalProps) => {
    const { t } = useTranslation()
    const [formInstance] = Form.useForm()
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)
    const updateVendorRuleMutation = useUpdateVendorRule()

    useEffect(() => {
        if (isShowing) {
            const initialVendorFormRule = VendorService.getInitialVendorFormRule(vendor)
            formInstance.setFieldsValue(initialVendorFormRule)
        }

        return () => {
            if (isShowing) {
                formInstance.resetFields()
            }
        }
    }, [isShowing, vendor?.id])

    const handleHide = () => {
        setIsLoading(false)
        formInstance.resetFields()
        onCancel(false)
    }

    const handleSubmit = async (values: Store) => {
        setIsLoading(true)
        const vendorRule = values as VendorRule
        try {
            await updateVendorRuleMutation.mutateAsync({ vendorId: vendor.id, vendorRule })
            onSubmit?.(values)
            handleHide()
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.updated.title"), t("notification:vendor.updated.message"))
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:vendor.edit.title"))
        }
    }

    return (
        <Modal
            destroyOnClose
            afterClose={() => formInstance.resetFields()}
            open={isShowing}
            maskClosable
            title={t("label:set_vendor_rule")}
            onCancel={handleHide}
            closable
            keyboard
            className="ant-modal--small"
            footer={[
                <Button key="back" onClick={handleHide}>
                    {t("action:cancel")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isLoading}
                    onClick={() => {
                        formInstance.submit()
                    }}
                >
                    {t("action:vendor.set_rule")}
                </Button>,
            ]}
        >
            <VendorRuleForm formInstance={formInstance} vendor={vendor} onSubmit={handleSubmit} />
        </Modal>
    )
}

// TODO get rid of default export
